import compact from 'lodash/compact';
import concat from 'lodash/concat';
import { createCustomSlice } from 'core/utils';
import i18n from 'i18nextInstance';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import {
    getAdditionalValues,
    getPaymentStatus,
    getTurnStatus,
    parseAppointmentStatusLabel,
    parseUserType
} from 'shared/utils';

const initialState = {
    agenda: {},
    agendaError: '',
    agendas: [],
    agendasLoading: false,
    appointmentType: [],
    appointmentTypes: [],
    appointmentTypesLoading: false,
    audit: [],
    auditLoading: false,
    availableAgenda: { agendas: [], availables: [], unavailables: [] },
    availableAgendaLoading: false,
    availableTimes: [],
    availableTimesLoading: false,
    creationError: {},
    downloadList: [],
    downloadStatus: null,
    eventTurns: [],
    externalAppointment: {},
    externalAppointmentLoading: false,
    filters: {},
    list: [],
    listLoading: false,
    scheduleLoading: false,
    selectedDoctorId: null,
    selectedOfficeId: null,
    selectedSpecialtyId: null,
    selectedTurn: null,
    selectedTurnLoading: false,
    status: '',
    unavailableTimes: [],
    unavailableTimesLoading: false
};

const turnsSlice = createCustomSlice({
    initialState,
    name: 'turns',
    reducers: {
        setTurnsAudit (state, { payload }) {
            state.audit = parseTurnsAudit(payload);
        },
        setTurnsDownloadList (state, { payload }) {
            state.downloadList = parseDownloadTurns(orderBy(payload, ['day', 'from_time']));
        },
        setTurnsList (state, { payload }) {
            state.eventTurns = parseTurns(payload);
            state.list = payload;
        }
    },
    saga: [
        'cancelTurn',
        'createAgenda',
        'createAppointmentType',
        'createAvailableTime',
        'createExternalAppointment',
        'createTurn',
        'createUnavailableTime',
        'deleteAgenda',
        'deleteAppointmentType',
        'deleteAvailableTime',
        'deleteUnavailableTime',
        'editTurn',
        'getAgenda',
        'getAgendas',
        'getAppointmentType',
        'getAppointmentTypes',
        'getAvailableAgenda',
        'getAvailableTimes',
        'getSchedule',
        'getSelectedTurn',
        'getTurnAudit',
        'getTurnsDownloadList',
        'getUnavailableTimes',
        'handleAppointmentsDelete',
        'updateAgenda',
        'updateAppointmentType',
        'updateSingleAppointment'
    ]
});

export const turnsActions = turnsSlice.actions;

export const parseDownloadTurns = list => list.map(({
    admitted_at,
    appointment_type_name = '-',
    cancellation_reason,
    comment,
    created_at,
    day,
    doctor_first_name = '',
    doctor_last_name = '',
    doctor_specialties,
    finalized_at,
    from_time = '',
    health_insurance,
    inroom_at,
    medical_coverage_name,
    office_name = '-',
    overshift,
    patient_company_name,
    patient_first_name,
    patient_health_insurance,
    patient_identification,
    patient_last_name,
    patient_medical_coverage_name,
    patient_plan_name,
    payment_status,
    plan_name,
    specialty_name,
    status,
    to_time = '',
    type,
    user_first_name,
    user_last_name,
    user_type
}) => ({
    admitted_at,
    appointment_type_name,
    cancellation_reason: cancellation_reason ?? '-',
    comment,
    created_at_day: moment(created_at).format('DD/MM/YYYY'),
    created_at_hour: moment(created_at).format('HH:mm'),
    day: moment(day).format('DD/MM/YYYY'),
    doctor_first_name,
    doctor_last_name,
    finalized_at,
    from_time: from_time.slice(0, 5),
    health_insurance: health_insurance ?? patient_health_insurance ?? '-',
    inroom_at,
    medical_coverage: medical_coverage_name ?? patient_medical_coverage_name ?? '-',
    office_name,
    overshift: !!overshift ? i18n.t('Si') : i18n.t('No'),
    patient_company_name: patient_company_name ?? '',
    patient_first_name,
    patient_identification,
    patient_last_name,
    payment_status: getPaymentStatus(payment_status),
    plan: plan_name ?? patient_plan_name ?? '-',
    specialties: specialty_name ?? (doctor_specialties ? doctor_specialties.map(name => name).join(', ') : ''),
    status: getTurnStatus(type === 'Evolution' ? type : status),
    to_time,
    user: user_first_name && user_last_name ? `${ user_first_name }, ${ user_last_name }` : '-',
    user_type: parseUserType(user_type)
}));

const parseTurns = ({ turns, unavailables }) => {
    const turnListing = turns.map(parseSingleTurn);
    const unavailablesListing = unavailables.map(({
        all_day,
        from_date,
        from_time,
        id,
        reason,
        to_date,
        to_time
    }) => {
        if (!all_day && from_date === to_date) {
            return {
                end: moment(`${ from_date } ${ to_time }`, 'YYYY-MM-DD HH:mm').toDate(),
                resource: {
                    event: 'unavailable',
                    id
                },
                start: moment(`${ from_date } ${ from_time }`, 'YYYY-MM-DD HH:mm').toDate(),
                title: `${ i18n.t('No disponible') }: ${ reason }`
            };
        } else {
            return null;
        }
    });
    const returnList = compact(concat(turnListing, unavailablesListing));
    return returnList;
};

const getStatusIcon = status => {
    switch (status) {
    case ('absent'): return '\u2026';
    case ('cancelled'): return '\u2717';
    case ('finalized'): return '\u2713';
    case ('overshift'): return '\uFF0B';
    case ('pending'): return '\u003F';
    default: return '';
    }
};

const getPaymentStatusIcon = paymentStatus => {
    switch (paymentStatus) {
    case ('coverage'): return '(C)';
    case ('completed'): return '($)';
    case ('completed_by_admin'): return '(A)';
    case ('completed_by_doctor'): return '(P)';
    case ('notrequired'): return '(-)';
    default: return '';
    }
};

export const parseSchedule = ({
    turns: {
        agendas = [],
        availableTimes = [],
        unavailableTimes = []
    } = {}
}) => {
    const newAgendas = agendas.map(agenda => ({ ...agenda, type: 'agenda' }));
    const newAvailableTimes = availableTimes.map(available => ({ ...available, type: 'available' }));
    const newUnavailableTimes = unavailableTimes.map(unavailable => ({ ...unavailable, type: 'unavailable' }));
    return orderBy(
        [...newAgendas, ...newAvailableTimes, ...newUnavailableTimes],
        ['from_date', 'to_date'],
        ['desc', 'desc']
    );
};

const parseAdmissionMessages = list => list.map(({ user: { first_name, last_name }, message, timestamp, ...rest }) => ({
    ...rest,
    created_at: timestamp,
    name: `${ last_name }, ${ first_name }`,
    text: message ?? ''
}));

const parseAdmissionRejections = list => list.map(({ doctor_first_name, doctor_last_name, timestamp, ...rest }) => ({
    ...rest,
    created_at: timestamp,
    highlighted: 1,
    name: `${ doctor_last_name }, ${ doctor_first_name }`,
    text: i18n.t('Admisión rechazada')
}));

export const parseSelectedTurn = list => {
    const { admission_messages, admissions_rejections } = list ?? {};
    const admissionsMessages = !isEmpty(admission_messages) ? parseAdmissionMessages(admission_messages) : [];
    const admissionsRejections = !isEmpty(admissions_rejections) ? parseAdmissionRejections(admissions_rejections) : [];
    return ({
        ...list,
        messages: orderBy([...admissionsRejections, ...admissionsMessages], ['created_at'])
    });
};

export const parseSingleTurn = ({
    appointment_type,
    comment,
    day,
    id,
    from_time,
    office = {},
    overshift,
    patient,
    patient: {
        first_name,
        last_name
    },
    payment_status,
    recurrence_id,
    same_office,
    status,
    to_time,
    type
}) => {
    const icon = getStatusIcon(status);
    const name = `${ last_name }, ${ first_name }`;
    const paymentIcon = getPaymentStatusIcon(payment_status);
    const officeName = !same_office && !!office?.name ? ` (${ office.name })` : '';
    const recurrence = !!recurrence_id ? ' (R)' : '';
    return ({
        end: moment(`${ day } ${ to_time }`, 'YYYY-MM-DD HH:mm:ss').toDate(),
        resource: {
            appointment_type: !!appointment_type ? appointment_type : {},
            comment,
            event: (same_office && !recurrence_id && status !== 'pending') ? 'turn' : 'display',
            id,
            overshift,
            patient,
            payment_status,
            recurrence_id,
            same_office,
            status,
            type
        },
        start: moment(`${ day } ${ from_time }`, 'YYYY-MM-DD HH:mm:ss').toDate(),
        title: `${ icon } ${ name } ${ paymentIcon }${ officeName }${ recurrence }`
    });
};

const parseAppointmentAction = action => {
    switch (action) {
    case ('AppointmentAdmissionAccepted'): return i18n.t('Admisión aceptada');
    case ('AppointmentAdmissionDeclined'): return i18n.t('Admisión rechazada');
    case ('AppointmentAdmissionPending'): return i18n.t('Admisión pendiente');
    case ('AppointmentCancelled'): return i18n.t('Cancelación');
    case ('AppointmentCreated'): return i18n.t('Creación');
    case ('AppointmentDeleted'): return i18n.t('Eliminación');
    case ('AppointmentUpdated'): return i18n.t('Modificación');
    case ('AppointmentReassigned'): return i18n.t('Reasignación');
    case ('AppointmentValidated'): return i18n.t('Validación');
    default: return i18n.t('Desconocida');
    }
};

const parseAppointmentAdditional = ({
    action,
    additional_information
}) => {
    const additional = getAdditionalValues(additional_information);
    const {
        comment,
        day,
        doctor_id,
        doctor_last_name,
        doctor_first_name,
        method,
        payment_status,
        office_id,
        office_name,
        status,
        time
    } = additional ?? {};
    const doctor_name = `${ doctor_last_name }, ${ doctor_first_name } [${ doctor_id }]`;
    const paymentLabel = paymentStatus => {
        switch (paymentStatus) {
        case ('completed'): return i18n.t('completo');
        case ('completed_by_admin'): return i18n.t('por administrador');
        case ('completed_by_doctor'): return i18n.t('por profesional');
        default: return i18n.t('pendiente');
        }
    };
    const getAdditional = () => {
        switch (action) {
        case ('AppointmentAdmissionAccepted'):
        case ('AppointmentAdmissionDeclined'):
        case ('AppointmentAdmissionPending'): return ` [${ i18n.t('Profesional') }: ${ doctor_name }]`;
        case ('AppointmentCancelled'): return `${ i18n.t('Comentario') }: ${ additional.cancellation_reason ?? '-' }`;
        case ('AppointmentDeleted'): return '';
        case ('AppointmentCreated'):
        case ('AppointmentUpdated'): return `
            ${ action === 'AppointmentCreated' ? i18n.t('Datos') : i18n.t('Cambios') } ->
            ${ action === 'AppointmentCreated' ? ` [${ i18n.t('Profesional') }: ${ doctor_name }]` : '' }
            ${ status != null ? ` [${ i18n.t('Estado') }: ${ parseAppointmentStatusLabel(status) }]` : '' }
            ${ day != null ? ` [${ i18n.t('Día') }: ${ moment(day, 'YYYY-MM-DD').format('DD/MM/YYYY') }]` : '' }
            ${ time != null ? ` [${ i18n.t('Hora') }: ${ time.replace('-', ':') }]` : '' }
            ${ comment != null ? ` [${ i18n.t('Comentario') }: ${ comment }]` : '' }
            ${ office_name != null ? ` [${ i18n.t('Consultorio') }: ${ office_name } - ${ office_id }]` : '' }
            ${ payment_status != null ? ` [${ i18n.t('Pago') }: ${ paymentLabel(payment_status) }]` : '' }
        `;
        case ('AppointmentReassigned'): return `
            Profesional: ${ doctor_name }
        `;
        default: return '';
        }
    };
    const getMethod = () => {
        switch (method) {
        case ('external'): return `[${ i18n.t('Externo') }] `;
        case ('ipn'): return '[IPN] ';
        case ('ws'): return '[WS] ';
        default: return '';
        }
    };
    return `${ getMethod() }${ getAdditional() }`;
};

const parseTurnsAudit = (list = []) => list.map(item => {
    const {
        action,
        datetime,
        user,
        user: {
            first_name,
            id,
            last_name,
            type
        } = {}
    } = item;
    return {
        action: parseAppointmentAction(action),
        additional_information: parseAppointmentAdditional(item),
        datetime,
        user: user ? `${ last_name }, ${ first_name } [${ id }] (${ parseUserType(type) })` : i18n.t('Ninguno')
    };
});

export default turnsSlice.reducer;
